import {Button, Card, CardContent, Grid, InputAdornment, useTheme,} from "@mui/material";
import React, {MouseEventHandler, useContext, useEffect, useState,} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {chargingWayAtom} from "../../../state/constantsRecoil";
import {AncillaryEntityResponse, AncillarySelectFE, ServiceItemObject,} from "../../../api/types/ancillary";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import CustomBanner from "../../../common/components/CustomBanner";
import {useFormContext} from "react-hook-form";
import {FormAutocomplete, FormCustomSwitch, FormDateTimePicker, FormTextField,} from "../../../common/form-fields";
import {SelectElementModelBE, SelectElementModelFE,} from "../../../api/types/generic";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {
    ancillaryExceptionSetFormValues,
    fromAncillaryBeToFe,
    fromGenericBeToGenericFe,
    i18nConvertNumberWithDecimals,
    setFormNumberValue,
} from "../../../utils";
import {CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {
    AncillaryExceptionBaseDefinitionFormValues,
    AncillaryExceptionEntityResponse
} from "../../../api/types/ancillaryException";
import {useAncillaryRepository} from "../../../api/repositories/AncillaryRepository";
import {initialAncillaryExceptionBaseDefinitionValues} from "./AncillaryExceptionBaseDefinitionForm";
import {unitsAtom} from "../../../state/rentalPeriodsRecoil";
import {useProviderRepository} from "../../../api/repositories/ProviderRepository";
import {
    numberIntegerRegex,
    numberIntegerWithNegativeValuesRegex, numberRegex,
    numberWithNegativeValuesRegex,
} from "../../../constants";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {ExceptionListEntityResponse} from "../../../api/types/exceptionList";
import {useExceptionListRepository} from "../../../api/repositories/ExceptionListRepository";
import {exceptionPackAtom} from "../../../state/ancillariesRecoil";
import {ancillaryExceptionAtom} from "../../../state/ancillaryExceptionRecoil";
import {CommercialSegmentEntityResponse} from "../../../api/types/commercialSegment";

const AncillaryExceptionBaseDefinition = ({
                                              originalCommercialSegment,
                                              ancillaryExceptionInUse,
                                              readOnly,
                                              handleSubmit,
                                          }: {
    originalCommercialSegment: SelectElementModelFE | null;
    ancillaryExceptionInUse: boolean;
    readOnly: boolean;
    handleSubmit: MouseEventHandler<HTMLButtonElement>;
}) => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["tranlsation, pricing"]);

    // Use Recoil
    const [commericalCategoryList, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [commericalSegmentList] = useRecoilState(commercialSegmentListAtom);
    const [chargingWayList] = useRecoilState(chargingWayAtom);
    const [units] = useRecoilState(unitsAtom);
    const [, setExceptionPack] = useRecoilState<ExceptionListEntityResponse | null>(exceptionPackAtom)
    const [ancillaryException] = useRecoilState<AncillaryExceptionEntityResponse | null>(ancillaryExceptionAtom);
    const [serviceItem, setServiceItem] = useState<any>([]);


    // Use State
    const [initialValues] = useState<AncillaryExceptionBaseDefinitionFormValues>(initialAncillaryExceptionBaseDefinitionValues);
    const [ancillaryList, setAncillaryList] = useState<AncillarySelectFE[]>([]);
    const [ancillaryPack, setAncillaryPack] = useState<SelectElementModelFE[]>([]);

    // Use Repository Hooks
    const {getCommercialCategoriesByCommercialSegmentId} = useCommercialCategoryRepository();
    const {getAncillariesBySegmentId, getServiceItem} = useAncillaryRepository();
    const {getProviders} = useProviderRepository();
    const {getExceptionListsToPackByCommercialSegmentId} = useExceptionListRepository();

    // Use Form Context
    const {formState, watch, getValues, setValue, register, reset} =
        useFormContext<AncillaryExceptionBaseDefinitionFormValues>();
    const watchAllFields = watch();
    const watchedLegalEntity = watch("legalEntity");

    // State per categorie originali (non filtrate per label)
    const [commercialCategoryListOriginal, setCommercialCategoryListOriginal] = useState<CommercialCategorySelectFE[]>([]);

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.id,
            label: el.name,
        })
    );
    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.label !== "all";
        })
        .map((el) => el.label);

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.id,
            label: el.name,
        })
    );

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.label)
        .filter((el) => el !== "all");

    const dealerFilteredOptions = dealerOption
        .map((el) => el.label)
        .filter((el) => el !== "all");

    // Effect per filtrare le categorie quando cambia la legal entity
    useEffect(() => {
        const legalEntityCode = ((watchedLegalEntity as SelectElementModelFE)?.code as string) 
            ?? (user.legalEntities?.length === 1 ? user.legalEntities[0].name : null);
        
        if (!legalEntityCode || commercialCategoryListOriginal.length === 0) {
            setCommercialCategoryList([]);
            return;
        }

        // Espandi le categorie in base alla legal entity selezionata
        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        commercialCategoryListOriginal.forEach(category => {
            category.labels?.forEach(labelObj => {
                if (labelObj.country === legalEntityCode) {
                    const key = labelObj.label;
                    if (!expandedCategories.has(key)) {
                        expandedCategories.set(key, {
                            ...category,
                            label: labelObj.label
                        });
                    }
                }
            });
        });
        setCommercialCategoryList(Array.from(expandedCategories.values()));
    }, [watchedLegalEntity, commercialCategoryListOriginal, user.legalEntities]);

    // Use Effect
    useEffect(() => {
        const getContent = async () => {
            if (originalCommercialSegment) {
                const commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId((originalCommercialSegment as SelectElementModelFE).code as string);
                const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
                setCommercialCategoryListOriginal(commercialCategoryList);
            }
            setValue("exceptionListPack", {
                code: (ancillaryException?.exceptionListPack as ExceptionListEntityResponse)?.id as number,
                label: (ancillaryException?.exceptionListPack as ExceptionListEntityResponse)?.name as string
            })
        };
        getContent();
    }, [originalCommercialSegment]);

    const handleChangeCommercialSegment = async (value: SelectElementModelFE) => {
        if (value) {
            setValue("commercialSegment", value);
            const commercialCategoriesData =
                await getCommercialCategoriesByCommercialSegmentId(
                    (value as SelectElementModelFE).code as string
                );
            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            setCommercialCategoryListOriginal(commercialCategoryList);

            const ancillariesData: AncillaryEntityResponse[] = (
                await getAncillariesBySegmentId({
                    commercialSegmentId: value.code as number,
                    showOnlyActive: true,
                    withoutOw: true,
                    dealers: dealerFilteredOptions,
                    legalEntities: everyLegalEntitiesWithoutAll,
                })
            ) as AncillaryEntityResponse[];
            const ancillaryList: AncillarySelectFE[] = fromAncillaryBeToFe(ancillariesData);
            setAncillaryList(ancillaryList);

            reset({
                ...initialValues,
                commercialSegment: value
            });
        } else {
            setValue("commercialSegment", value);
            setValue("commercialCategory", null);
            setCommercialCategoryListOriginal([]);
        }
    };

    const handleChangeAncillary = async (value: AncillarySelectFE) => {
        //FIX FORM SET VALUE
        if (value) {
            setValue("prcAncillary", value);
            setValue("code", value.ancillaryCode);
            ancillaryExceptionSetFormValues(setValue, value);
            setValue("nameAncillary", value.nameAncillary as string);
            setValue("description", value.description);
            setValue("name", value.nameAncillary as string);
            setValue("tax", value.tax);
            setValue("type", value.type);
            setValue("subtype", value.subtype);
            setValue("cardinality", value.cardinality);
            setValue("amountMin", value?.amountMin ? i18nConvertNumberWithDecimals(Number(value.amountMin ) / 100) : null);
            setValue("amountMax", value?.amountMax ? i18nConvertNumberWithDecimals(Number(value.amountMax ) / 100) : null);
            setValue("sellDesk", value.sellDesk);
            setValue("passageToCounterNeeded", value.passageToCounterNeeded);
            setValue("toleranceTime", value.toleranceTime);
            // LPP-25456: la nuova eccezione parte dai flag dell'accessorio base, poi modificabili
            setValue("flagResellableAtDeskPostRa", value.flagResellableAtDeskPostRa ?? false);
            setValue("flagResellableInSubstitution", value.flagResellableInSubstitution ?? false);
            setValue("flagDiscountApplicable", value.flagDiscountApplicable ?? false);
            setValue("timeToChange", value.timeToChange ?? null);
            setValue("timeToRemove", value.timeToRemove ?? null);
            setValue("additionalField", value.additionalField);
            setValue("applicationTiming", value.applicationTiming);
            setValue("taxCode", serviceItem.find((s: ServiceItemObject) => s.code === value.taxCode)?.name || value.taxCode);
            setValue("exceptionListPack", value.exceptionListPack ? {
                code: value.exceptionListPack?.id as number,
                label: value.exceptionListPack?.name as string
            } : null)
            setValue("commercialCategory", value.exceptionListPack ? {
                code: value.exceptionListPack?.commercialCategory.id as number,
                label: value.exceptionListPack?.commercialCategory.name as string,
                active: value.exceptionListPack?.commercialCategory.active as boolean,
                commercialSegment: value.exceptionListPack?.commercialCategory.commercialSegment as CommercialSegmentEntityResponse
            } : null)
            setValue("legalEntity", {
                code: value.legalEntity as string,
                label: t('translation:legalEntities.'.concat(value.legalEntity as string))
            })
            setValue("dealer", {
                code: value.dealer as string,
                label: t('translation:dealers.'.concat(value.dealer as string))
            })

            if (serviceItem.length === 0) {
                const services = await getServiceItem(value.legalEntity as string, value.dealer as string) as unknown as ServiceItemObject[];
                setServiceItem(services);
                setValue("taxCode", services.find((s: ServiceItemObject) => s.code === value.taxCode)?.name || "");
            }
        }
    };

    useEffect(() => {
        const getPacks = async () => {
            const segments = [(getValues("commercialSegment") as SelectElementModelFE)?.code] as string[]
            const exceptionListToPack = await getExceptionListsToPackByCommercialSegmentId(segments)
            const packs = exceptionListToPack.map(el => ({
                code: el.id,
                label: el.name
            }))
            setAncillaryPack(packs)
        }
        getPacks()
    }, [getValues("commercialSegment")])

    const handleChangePack = async (value: SelectElementModelFE) => {
        setValue("exceptionListPack", value)
        const segments = [(getValues("commercialSegment") as SelectElementModelFE)?.code] as string[]
        const exceptionListToPack = await getExceptionListsToPackByCommercialSegmentId(segments)
        const filtered: ExceptionListEntityResponse = exceptionListToPack.find(el => el?.id === value?.code) as ExceptionListEntityResponse
        setExceptionPack(filtered)
    }

    return (
        <Grid container spacing={2} alignItems="start">
            {!readOnly && ancillaryExceptionInUse &&
                <CustomBanner message={t("pricing:ancillaryException.banner")} severity={"warning"}/>
            }
            <Grid item xs={12} sm={12} md={12}>
                <Card>
                    <CardContent>
                        <Grid container spacing={2} py={2}>
                            {/* COMMERCIAL SEGMENT */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("commercialSegment")}
                                    label={`${t("pricing:ancillaryExceptionCreate.form.labels.commercialSegment")}*`}
                                    options={commericalSegmentList || []}
                                    handleChange={handleChangeCommercialSegment}
                                    isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                                    readOnly={!!getValues().id || formState.isSubmitting}
                                />
                            </Grid>
                            {/* ANCILLARY*/}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("prcAncillary")}
                                    label={`${t("pricing:ancillaryExceptionCreate.form.labels.ancillary")}*`}
                                    options={ancillaryList || []}
                                    handleChange={handleChangeAncillary}
                                    isOptionEqualToValue={(option: AncillarySelectFE, value: AncillarySelectFE) => option?.code === value?.code}
                                    readOnly={!!getValues().id || formState.isSubmitting}
                                />
                            </Grid>
                            {/*LEGAL ENTITY*/}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("legalEntity")}
                                    label={`${t("pricing:dealer.tenant")}`}
                                    options={[]}
                                    readOnly={true}
                                />
                            </Grid>
                            {/*DEALER*/}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register(`dealer`)}
                                    label={`${t("pricing:dealer.dealer")}`}
                                    options={[]}
                                    readOnly={true}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} py={2}>
                            {/* CODE */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("code")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.code")}`}
                                    readOnly={true}
                                />
                            </Grid>
                            {/* NAME */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("nameAncillary")}
                                    label={`${t("pricing:ancillary.form.labels.nameAncillary")}`}
                                    readOnly={true}
                                />
                            </Grid>
                            {/* CARDINALITY */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("cardinality")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.cardinality")}
                                    readOnly={true}
                                    type={"text"}
                                    handleChange={(value: string) => setFormNumberValue(value, setValue, "cardinality", numberIntegerRegex)}
                                />
                            </Grid>
                            {/* UNIT */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("tpUnit")}
                                    label={`${t("pricing:ancillary.form.labels.unit")}`}
                                    readOnly={true}
                                    options={units || []}
                                    isOptionEqualToValue={(option, value) => option?.code === value?.code}
                                />
                            </Grid>
                            {/* RESERVATION FROM */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("reservationFrom")}
                                    label={`${t("pricing:ancillary.form.labels.reservationFrom")}*`}
                                    readOnly={readOnly || formState.isSubmitting || ancillaryExceptionInUse}
                                />
                            </Grid>
                            {/* RESERVATION TO */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("reservationTo")}
                                    label={`${t("pricing:ancillary.form.labels.reservationTo")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    minDate={watchAllFields.reservationFrom || undefined}
                                />
                            </Grid>
                            {/* PICKUP DATE FROM */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("pickupFrom")}
                                    label={`${t("pricing:ancillary.form.labels.pickupDateFrom")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* PICKUP DATE TO */}
                            <Grid item xs={12} md={3}>
                                <FormDateTimePicker
                                    {...register("pickupTo")}
                                    label={`${t("pricing:ancillary.form.labels.pickupDateTo")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    minDate={watchAllFields.pickupFrom || undefined}
                                />
                            </Grid>
                            {/* ADDITIONAL FIELD */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("additionalField")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.additionalFieldType")}
                                    readOnly={true}
                                    options={["NUMBER", "STRING"]}
                                    isOptionEqualToValue={(option: string, value: string) => option === value}
                                />
                            </Grid>
                            {/* CURRENCY */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("currency")}
                                    label={`${t("pricing:ancillary.form.labels.currency")}`}
                                    readOnly={true}
                                    options={[]}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("amountMin")}
                                    label={`${t("pricing:ancillary.form.labels.minimumAmount")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => {
                                        setFormNumberValue(i18nConvertNumberWithDecimals(value), setValue, "amountMin", numberRegex);
                                    }}
                                />
                            </Grid>
                            {/* MAXIMUM AMOUNT */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("amountMax")}
                                    label={`${t("pricing:ancillary.form.labels.maximumAmount")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => {
                                        setFormNumberValue(i18nConvertNumberWithDecimals(value), setValue, "amountMax", numberRegex);
                                    }}
                                />
                            </Grid>
                            {/* CHARGING WAY */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("chargingWay")}
                                    label={`${t("pricing:ancillary.form.labels.chargingWay")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    options={chargingWayList}
                                />
                            </Grid>
                            {/* TIME UNITS MAX */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("maxUnits")}
                                    label={`${t("pricing:ancillary.form.labels.maxUnits")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => setFormNumberValue(value, setValue, "maxUnits", numberIntegerRegex)}
                                />
                            </Grid>
                            {/* CHARGE TYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("chargeType")}
                                    label={`${t("pricing:ancillary.form.labels.chargeType")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                    options={["VALUE", "PERCENTAGE"]}
                                />
                            </Grid>
                            {/* TAX */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("tax")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.tax")}
                                    readOnly={true}
                                />
                            </Grid>
                            {/* TYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("type")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.type")}`}
                                    options={[]}
                                    isOptionEqualToValue={(option: string, value: string) => option === value}
                                    readOnly={true}
                                />
                            </Grid>
                            {getValues("type") === "Pack" &&
                                <Grid item xs={12} md={3}>
                                    <FormAutocomplete
                                        {...register("exceptionListPack")}
                                        label={`${t("pricing:ancillaryLevelOne.form.labels.exceptionList")}*`}
                                        options={ancillaryPack}
                                        isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                                        readOnly={readOnly || formState.isSubmitting}
                                        handleChange={(value: SelectElementModelFE) => handleChangePack(value)}
                                    />
                                </Grid>}
                            {/* SUBTYPE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("subtype")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.subtype")}`}
                                    options={[]}
                                    isOptionEqualToValue={(option: string, value: string) => option === value}
                                    readOnly={true}
                                />
                            </Grid>
                            {/* APPLICATION TIMING */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("applicationTiming")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.applicationTiming")}
                                    readOnly={true}
                                    options={["PICK UP", "DROP OFF"]}
                                    isOptionEqualToValue={(option: string, value: string) => option === value}
                                />
                            </Grid>
                            {/* TOLERANCE TIME */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("toleranceTime")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.toleranceTime")}
                                    readOnly={true}
                                    type={"text"}
                                    handleChange={(value: string) => setFormNumberValue(value, setValue, "toleranceTime", numberIntegerRegex)}
                                />
                            </Grid>
                            {/* SELL DESK */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("sellDesk")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.sellDesk")}
                                    readOnly={true}
                                />
                            </Grid>
                            {/* TAXCODE */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("taxCode")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.taxCode")}`}
                                    readOnly={true}
                                    options={serviceItem.map((item : any) => ({
                                        label: item.name,
                                        value: item.code,
                                    }))}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} py={2}>
                            {/* EXCEPTION NAME */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("name")}
                                    label={`${t("pricing:ancillaryExceptionCreate.form.labels.exceptionName")}*`}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* DESCRIPTION */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("description")}
                                    label={`${t("pricing:ancillaryLevelOne.form.labels.description")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* COMMERCIAL CATEGORY */}
                            <Grid item xs={12} md={3}>
                                <FormAutocomplete
                                    {...register("commercialCategory")}
                                    label={`${t("pricing:ancillaryExceptionCreate.form.labels.commercialCategory")}*`}
                                    options={commericalCategoryList || []}
                                    isOptionEqualToValue={(option, value) => option?.code === value?.code}
                                    readOnly={!!getValues().id || formState.isSubmitting || !watchAllFields.commercialSegment}
                                />
                            </Grid>
                            {/* PASSAGE TO COUNTER */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("passageToCounterNeeded")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.passageToCounterNeeded")}
                                    readOnly={true}
                                />
                            </Grid>
                            {/* LPP-25456 - 5 flag ancillary (RF-02) propri dell'eccezione */}
                            {/* FLAG RESELLABLE AT DESK POST RA */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("flagResellableAtDeskPostRa")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.flagResellableAtDeskPostRa")}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* FLAG RESELLABLE IN SUBSTITUTION */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("flagResellableInSubstitution")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.flagResellableInSubstitution")}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* FLAG DISCOUNT APPLICABLE */}
                            <Grid item xs={12} md={3}>
                                <FormCustomSwitch
                                    {...register("flagDiscountApplicable")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.flagDiscountApplicable")}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                            {/* TIME TO CHANGE (intero nullable: null = nessun limite, 0 = disabilita) */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("timeToChange")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.timeToChange")}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => setFormNumberValue(value, setValue, "timeToChange", numberIntegerRegex)}
                                />
                            </Grid>
                            {/* TIME TO REMOVE (intero nullable: null = nessun limite, 0 = disabilita) */}
                            <Grid item xs={12} md={3}>
                                <FormTextField
                                    {...register("timeToRemove")}
                                    label={t("pricing:ancillaryLevelOne.form.labels.timeToRemove")}
                                    readOnly={readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => setFormNumberValue(value, setValue, "timeToRemove", numberIntegerRegex)}
                                />
                            </Grid>
                            {/*<Grid item xs={ 12 } md={ 12 } sx={ { display: "flex", alignItems: "flex-end" } }>*/}
                            {/*    <RadioGroupComponent*/}
                            {/*        { ...register("discountIncreaseSelected") }*/}
                            {/*        value={ discountIncreaseSelected }*/}
                            {/*        handleChange={ handleChangeRadioGroup }*/}
                            {/*        options={ radioButtonOptions }*/}
                            {/*        disabled={ !!getValues().id || readOnly || formState.isSubmitting }*/}
                            {/*    />*/}
                            {/*</Grid>*/}
                            {/* DISCOUNT INCREASE PERCENTAGE */}
                            <Grid item xs={12} md={6}>
                                <FormTextField
                                    {...register("discountIncreasePercentage")}
                                    label={`${t("pricing:ancillaryExceptionCreate.form.labels.discountIncreasePercentage")}`}
                                    readOnly={!!getValues().id || readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => {
                                        setFormNumberValue(value, setValue, "discountIncreasePercentage", numberIntegerWithNegativeValuesRegex);
                                    }}
                                    startAdornment={<InputAdornment position="start">%</InputAdornment>}
                                />
                            </Grid>
                            {/* DISCOUNT INCREASE FIXED */}
                            <Grid item xs={12} md={6}>
                                <FormTextField
                                    {...register("discountIncreaseFixed")}
                                    label={`${t("pricing:ancillaryExceptionCreate.form.labels.discountIncreaseFixed")}`}
                                    readOnly={!!getValues().id || readOnly || formState.isSubmitting}
                                    type={"text"}
                                    handleChange={(value: string) => {
                                        setFormNumberValue(i18nConvertNumberWithDecimals(value), setValue, "discountIncreaseFixed", numberWithNegativeValuesRegex);
                                    }}
                                />
                            </Grid>
                            {/* NOTE */}
                            <Grid item xs={12} md={12}>
                                <FormTextField
                                    {...register("note")}
                                    label={`${t("pricing:ancillary.form.labels.notes")}`}
                                    readOnly={readOnly || formState.isSubmitting}
                                />
                            </Grid>
                        </Grid>
                        {!readOnly &&
                            <Grid container item xs={12} justifyContent="flex-end" columnGap={2}>
                                <Grid>
                                    <Button variant="outlined"
                                            sx={{marginRight: "15px"}}
                                            color={"accent"}
                                            type={"button"}
                                            disabled={formState.isSubmitting}
                                            onClick={() => window.close()}
                                    >
                                        {t("pricing:actions.cancel")}
                                    </Button>
                                    <Button color={"accent"}
                                            type={"button"}
                                            onClick={handleSubmit}
                                            sx={{border: `1px solid ${theme.palette.accent.main}`}}
                                            disabled={formState.isSubmitting}
                                    >
                                        {t("pricing:actions.proceed")}
                                    </Button>
                                </Grid>
                            </Grid>
                        }
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default AncillaryExceptionBaseDefinition;
